import React, { useState, useCallback } from 'react';
import Code from '../../Code/Code';
import EmbedCodeCopyButton from '../../EmbedCodeCopyButton/EmbedCodeCopyButton';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import getHostUrlForIntegrations from '../../../../../services/mini/getHostUrlForIntegrations';

const androidMavenDependencyCode = 'implementation \'app.alan:sdk:4.12.0\'';

const androidInsertBtnCode = `<com.alan.alansdk.button.AlanButton
android:id="@+id/alan_button"
android:layout_width="match_parent"
android:layout_height="wrap_content"
android:layout_gravity="bottom"
android:visibility="visible"
app:button_horizontal_align="right"
app:layout_constraintBottom_toBottomOf="parent"
app:layout_constraintEnd_toEndOf="parent"
/>
`;

function getJavaCode(alanKey, codeMode) {
  if (codeMode === 'simple') {
    return `/// Add AlanButton variable    
private AlanButton alanButton;

...

/// Define the project key
AlanConfig config = AlanConfig.builder().setServer("${getHostUrlForIntegrations()}").setProjectId("${alanKey}").build();
alanButton = findViewById(R.id.alan_button);
alanButton.initWithConfig(config);
        
AlanCallback alanCallback = new AlanCallback() {
  /// Handle commands from Alan AI Studio
  @Override
  public void onCommand(final EventCommand eventCommand) {
    try {
      JSONObject command = eventCommand.getData();
        String commandName = command.getJSONObject("data").getString("command");
        Log.d("AlanButton", "onCommand: commandName: " + commandName);
    } catch (JSONException e) {
      Log.e("AlanButton", e.getMessage());
    }
  }
};

/// Register callbacks
alanButton.registerCallback(alanCallback);
`;
  } else {
    return `/// Add AlanButton variable    
private AlanButton alanButton;

...

/// Define the project key and additional params
JSONObject params = new JSONObject();
try {
  params.put("data","your auth data if needed");
} catch (JSONException e) {
  Log.e("AlanButton", e.getMessage());
}
AlanConfig config = AlanConfig.builder().setServer("${getHostUrlForIntegrations()}").setProjectId("${alanKey}").setDataObject(params).build();
alanButton = findViewById(R.id.alan_button);
alanButton.initWithConfig(config);
        
AlanCallback alanCallback = new AlanCallback() {
  /// Handle commands from Alan AI Studio
  @Override
  public void onCommand(final EventCommand eventCommand) {
    try {
      JSONObject command = eventCommand.getData();
        String commandName = command.getJSONObject("data").getString("command");
        Log.d("AlanButton", "onCommand: commandName: " + commandName);
    } catch (JSONException e) {
      Log.e("AlanButton", e.getMessage());
    }
  }

  /// Handle events
  @Override
  public void onEvent(String payload) {
    try {
      JSONObject eventDict = new JSONObject(payload);
      Log.d("AlanButton", "onEvent: event: " + eventDict);
    } catch (JSONException e) {
      Log.e("AlanButton", e.getMessage());
    }
  }

  /// Handle button state
  @Override
  public void onButtonState(AlanState alanState) {
    Log.d("AlanButton", "onButtonState: " + alanState);
  }
};

/// Register callbacks
alanButton.registerCallback(alanCallback);
`;
  }
}
    
function getJavaApiCode() {
  return `/// Activate Alan Button programmatically
void activate() {
  alanButton.activate();
}
      
/// Deactivate Alan Button programmatically
void deactivate() {
  alanButton.deactivate();
}
      
/// Play any text via Alan Button
void playText() {
  /// Provide text as string param
  alanButton.playText("Hi");
}
      
/// Execute any command locally
void playCommand() {
  /// Provide any params
  JSONObject params = new JSONObject();
  try {
    params.put("action","openSomePage");
  } catch (JSONException e) {
    Log.e("AlanButton", e.getMessage());
  }
  alanButton.playCommand(params.toString(), null);
}
      
/// Call projectAPI.funcName from Alan AI Studio script
void callProjectApi() {
  /// Provide any params
  JSONObject params = new JSONObject();
  try {
    params.put("data","your data");
  } catch (JSONException e) {
    Log.e("AlanButton", e.getMessage());
  }
  alanButton.callProjectApi("script::funcName", params.toString());
}
      
/// Set up a visual state
void setVisualState() {
  /// Provide any params
  JSONObject params = new JSONObject();
  try {
    params.put("data","your data");
  } catch (JSONException e) {
    Log.e("AlanButton", e.getMessage());
  }
  alanButton.setVisualState(params.toString());
}
`;
}

const codeModes = [
  { name: 'Simple Example', key: 'simple' },
  { name: 'Advanced Example', key: 'advanced' }
];

export function AndroidTab({ alanKey }) {

    const [codeMode, setCodeMode] = useState('simple');

    const changeCodeModeCb = useCallback((codeModeObj) => {
      setCodeMode(codeModeObj.key);
    }, []);

    return <div className="embed-code-tab">
        <div className="embed-code-tab_line">
                <div className="embed-code-tab_description">
                </div>
                <div className="embed-code-tab_dash"></div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_not-highlighted-code">
                        <Code>
                            {
                                '\n'
                            }
                        </Code>
                    </div>
                </div>
            </div>
            <ComboBox values={codeModes}
                valueKey="key"
                valueName="name"
                classes="code-mode-combo-box light-blue-with-border"
                selectedValueId={codeMode}
                onValueSelect={changeCodeModeCb}
                renderItem={(codeModeItem) => {
                    return <div style={{ paddingLeft: '8px' }}
                        key={codeModeItem.key}>
                        {codeModeItem.name}
                    </div>;
                }} />
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Add Alan maven dependency
                </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted category="android" type="code">
                        {androidMavenDependencyCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={androidMavenDependencyCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Insert AlanButton into your XML layout anywhere
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted category="android" type="tag">
                        {androidInsertBtnCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={androidInsertBtnCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc7">
                Add AlanConfig and AlanButton to MainActivity
                </div>
            <div className="embed-code-tab_dash embed-code-desc7">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {getJavaCode(alanKey, codeMode)}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getJavaCode(alanKey, codeMode)} />
                    </div>
                </div>
            </div>
        </div>
        {(() => {
          if (codeMode === 'advanced') {
              return <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc7">
                    AlanButton common API
                    </div>
                <div className="embed-code-tab_dash embed-code-desc7">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <Code highlighted={true} category="ios" type="code">
                          {getJavaApiCode()}
                        </Code>
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={getJavaApiCode()} />
                        </div>
                    </div>
                </div>
            </div>;
          } else {
            return <></>;
          }
        })()}
    </div>;
}
